import { Color4, Engine, HemisphericLight, MeshBuilder, Scene, StandardMaterial, Texture, Vector3 } from "@babylonjs/core"
import canAutoPlay from "can-autoplay"
import { appElement, AppType } from "../../../common/AppType"
import { CCUtils } from "../../../common/CCUtils"
import { log } from "../../../common/Logging"
import { TextUtils } from "../../../common/TextUtils"
import { BabUtils } from "../../BabUtils"
import { MazeAudio } from "./assets/MazeAudio"
import { mazeImages } from "./assets/MazeImages"
import { Cameras, CameraType } from "./Cameras"
import { Gui } from "./Gui"
import { MazeCigs } from "./MazeCigs"
import { MazeStages } from "./MazeStages"
import { MazeStore } from "./MazeStore"
import { mazeBuilders } from "./MazeWalls"

export class Maze {

    mazeStore = MazeStore.newInstance()
    mazeAudio: MazeAudio

    constructor() {
        // Send home if not logged in
        BabUtils.sharedCreateFunctions()

        // Create canvas and engine.
        const docElement = document.documentElement
        this.mazeStore.canvas = appElement(AppType.BABYLON) as HTMLCanvasElement
        this.mazeStore.width = docElement.clientWidth
        this.mazeStore.height = docElement.clientHeight
        this.mazeStore.canvas.width = this.mazeStore.width
        this.mazeStore.canvas.height = this.mazeStore.height
        this.mazeStore.engine = new Engine(this.mazeStore.canvas, true)

        // Create the scene.
        this.createScene()

        // Start render loop.
        this.doRender()
    }

    createScene = async () => {

        const {engine} = MazeStore.instance

        // Create a basic BJS Scene object.
        const scene = new Scene(engine)
        MazeStore.instance.scene = scene

        scene.gravity = new Vector3(0, -0.5, 0)
        scene.collisionsEnabled = true

        const cameras = new Cameras()
        this.mazeStore.cameras = cameras

        // Sky color
        scene.clearColor = Color4.FromHexString("#78d3fdff")

        // Create a basic light, aiming 0,1,0 - meaning, to the sky.
        const light1 = new HemisphericLight("hemi", new Vector3(-100, 200, -50), scene)
        light1.intensity = 0.6

        const light2 = new HemisphericLight("hemi2", new Vector3(100, 200, 50), scene)
        light2.intensity = 0.6

        cameras.useCamera(CameraType.ARC_ROTATE)

        this.mazeStore.mazeWalls = mazeBuilders.exampleMazeWalls()

        // Create a built-in "ground" shape.
        const ground = MeshBuilder.CreateGround("ground", {width: 420, height: 420, subdivisions: 2}, scene)
        ground.checkCollisions = true
        const groundMaterial = new StandardMaterial("grass", scene)
        groundMaterial.diffuseTexture = new Texture(mazeImages.grass, scene)
        // No typescript def for these values?!
        groundMaterial.diffuseTexture["uScale"] = 15
        groundMaterial.diffuseTexture["vScale"] = 15
        ground.material = groundMaterial

        const titleGround = MeshBuilder.CreatePlane("titleGround", {width: 10, height: 10}, scene)
        titleGround.addRotation(Math.PI / 2, 0, 0)
        titleGround.position = new Vector3(-5, 0.01, -18)

        const titleMaterial = new StandardMaterial("titleMaterial", scene)
        titleMaterial.diffuseTexture = new Texture(mazeImages.title, scene)
        titleGround.material = titleMaterial
        titleMaterial.diffuseTexture.hasAlpha = true

        this.mazeAudio = MazeAudio.newInstance()
        this.mazeStore.stages = new MazeStages()

        TextUtils.loadFonts()
        this.mazeStore.gui = new Gui()
        if (!CCUtils.debug) {
            const can = await canAutoPlay.audio()
            if (!can.result) {
                log.debug(can)
                this.mazeStore.gui.fullScreenButton("Click to Start", () => this.mazeStore.stages.startMaze())
            } else {
                this.mazeStore.stages.startMaze()
            }
        }
    }

    doRender = () => {
        const {engine, scene} = MazeStore.instance

        // Run the render loop.
        engine.runRenderLoop(() => {
            const {mazeCigs, userStartedMaze, userDoneWithMaze} = MazeStore.instance
            scene.render()

            if (userStartedMaze && !userDoneWithMaze) {
                mazeCigs.needsSmoking()
            }
        })

        // The canvas/window resize event handler.
        window.addEventListener("resize", () => {
            engine.resize()
        })
    }

    dispose = () => {
        this.mazeStore.engine.stopRenderLoop()
        this.mazeStore.engine.dispose()
    }

    testMazeCigGlow = () => {
        if (CCUtils.debug) {
            const cigs = MazeCigs.forTestingRealMaze()
            cigs.cigs.forEach(cig => cigs.addSmokeAt(cig))
        }
    }

}
